import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Loader2, Users, CreditCard, TrendingUp } from "lucide-react";
import { cn } from "@/utils/functions/cn";
import { resellerApi } from "@/services/api";
import { toast } from "sonner";
import { t, type Lang } from "@/utils/i18n";
import { useLanguage } from "@/components/providers/language-provider";
import type { CountryCode } from "libphonenumber-js";
import { CountrySelect, PhoneInput, browserRegion, defaultCountry, formatNational, isPhoneValid, toInternational, useCountries } from "@/components/forms/country-phone";

const RegisterPage = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [company, setCompany] = useState("");
  const countries = useCountries(language);
  const [phone, setPhone] = useState("");
  // Country of residence (optional) and the phone's dial code; picking a country also sets the dial code.
  const [country, setCountry] = useState<CountryCode | undefined>(undefined);
  const [dialCountry, setDialCountry] = useState<CountryCode>(() => defaultCountry(language));
  const phoneInvalid = !isPhoneValid(phone, dialCountry);
  // Refine the dial-code guess from the browser locale once the country list is known.
  useEffect(() => {
    const region = browserRegion();
    if (!phone && region && countries.some((c) => c.code === region)) setDialCountry(region);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries.length]);
  // Only flag the phone once the user leaves the field (or submits), not while typing.
  const [phoneTouched, setPhoneTouched] = useState(false);
  const showPhoneError = phoneInvalid && phoneTouched;
  const [partnerType, setPartnerType] = useState<"reseller" | "referral">("reseller");

  const isFormValid = () => {
    return (
      name.trim().length >= 2 &&
      surname.trim().length >= 2 &&
      email.trim() !== "" &&
      password.length >= 6 &&
      password === confirmPassword &&
      !phoneInvalid
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneTouched(true);
    if (!isFormValid()) return;

    if (password !== confirmPassword) {
      toast.error(t("reseller", "passwordMismatch", language));
      return;
    }

    setLoading(true);
    try {
      await resellerApi.register({
        name: name.trim(),
        surname: surname.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone: toInternational(phone, dialCountry) || undefined,
        company: company.trim() || undefined,
        country: countries.find((c) => c.code === country)?.englishName || undefined,
        partnerType,
      });

      toast.success(t("reseller", "registerSuccess", language));
      const dashboardUrl = window.location.hostname.endsWith("moova.tv")
        ? "https://reseller.moova.tv"
        : "http://localhost:3003";
      const loginEmail = encodeURIComponent(email.trim().toLowerCase());
      window.location.href = `${dashboardUrl}/login?fresh=true&email=${loginEmail}&mode=reseller`;
    } catch (error: any) {
      const message = error?.message || t("reseller", "registerError", language);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "text-sm bg-white/[0.04] border-white/[0.08] placeholder:text-white/20 focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-0 focus-visible:border-brand-accent rounded-[12px] h-11";

  return (
    <>
      <Helmet>
        <title>Reseller Registration - MoovaTV</title>
        <meta name="description" content="Register as a MoovaTV reseller and start selling." />
      </Helmet>

      {/* One fold: pitch on the left, the whole registration form in one compact card on the right. */}
      <section className="px-4 sm:px-6 py-6 sm:py-8 lg:py-6 lg:min-h-[calc(100svh-var(--moova-header-height))] lg:flex lg:items-center">
        <div className="w-full max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-5 sm:gap-7 lg:gap-16 items-center">

          {/* ── Left: title, steps, benefits ── */}
          <div className="space-y-5 lg:space-y-7">
            <div>
              <h1 className="!text-[30px] sm:!text-[40px] lg:!text-[52px] font-semibold tracking-tight !leading-[1.08] !mt-0 !mb-2.5 sm:!mb-3">
                {t("reseller", "title", language)}
              </h1>
              <p className="text-[15px] sm:text-[17px] text-white/50 leading-relaxed max-w-md">
                {t("reseller", "subtitle", language)}
              </p>
            </div>

            <ol className="flex flex-col gap-2.5 sm:gap-3 lg:gap-4">
              {[t("reseller", "step1", language), t("reseller", "step2", language), t("reseller", "step3", language)].map((text, i) => (
                <li key={i} className="flex items-center gap-3 lg:gap-4">
                  <span className="h-8 w-8 lg:h-10 lg:w-10 shrink-0 grid place-items-center rounded-[9px] lg:rounded-[11px] bg-brand-accent/12 border border-brand-accent/25 text-[13px] lg:text-base font-semibold text-brand-accent">
                    {i + 1}
                  </span>
                  <span className="text-[14px] leading-snug text-white/70 lg:text-[16px] lg:text-white/75">{text}</span>
                </li>
              ))}
            </ol>

            <div className="hidden lg:grid grid-cols-3 gap-4">
              {[
                { icon: Users, title: t("reseller", "manageClients", language), text: t("reseller", "manageClientsDesc", language) },
                { icon: CreditCard, title: t("reseller", "competitivePricing", language), text: t("reseller", "competitivePricingDesc", language) },
                { icon: TrendingUp, title: t("reseller", "realTimeAnalytics", language), text: t("reseller", "realTimeAnalyticsDesc", language) },
              ].map(({ icon: Icon, title, text }) => (
                <div key={title} className="min-w-0">
                  <Icon className="h-4 w-4 text-brand-accent mb-2" />
                  <p className="text-[13px] font-medium leading-snug">{title}</p>
                  <p className="text-[12px] text-white/40 leading-snug mt-1">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: form card ── */}
          <div className="rounded-[20px] border border-white/[0.08] bg-white/[0.035] p-4 sm:p-6 lg:p-7">
            <div className="flex items-baseline justify-between gap-3 mb-4">
              <h2 className="text-base sm:text-lg font-semibold">{t("reseller", "createAccount", language)}</h2>
              <p className="text-[11px] text-white/35 text-right">{t("reseller", "fieldsRequired", language)}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3.5 sm:space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "nameLabel", language)} *</label>
                  <Input placeholder={t("reseller", "namePlaceholder", language)} value={name} onChange={(e) => setName(e.target.value)} className={inputClass} required />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "surnameLabel", language)} *</label>
                  <Input placeholder={t("reseller", "surnamePlaceholder", language)} value={surname} onChange={(e) => setSurname(e.target.value)} className={inputClass} required />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "emailLabel", language)} *</label>
                <Input type="email" placeholder={t("reseller", "emailPlaceholder", language)} value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "passwordLabel", language)} *</label>
                  <Input type="password" placeholder={t("reseller", "passwordPlaceholder", language)} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} required minLength={6} />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "confirmLabel", language)} *</label>
                  <Input type="password" placeholder={t("reseller", "confirmPlaceholder", language)} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClass} required />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-[10px] text-red-400">{t("reseller", "passwordMismatch", language)}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "companyLabel", language)} <span className="normal-case tracking-normal text-white/30">· {t("reseller", "optional", language).toLowerCase()}</span></label>
                <Input placeholder={t("reseller", "companyPlaceholder", language)} value={company} onChange={(e) => setCompany(e.target.value)} className={inputClass} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-3">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "countryLabel", language)}</label>
                  <CountrySelect
                    countries={countries}
                    value={country}
                    onChange={(c) => {
                      setCountry(c.code);
                      // Re-mask the digits already typed for the new dial code.
                      setDialCountry(c.code);
                      setPhone((p) => formatNational(p.replace(/\D/g, ""), c.code));
                    }}
                    placeholder={t("reseller", "countrySelect", language)}
                    searchPlaceholder={t("reseller", "countrySearch", language)}
                    emptyText={t("reseller", "countryNone", language)}
                    className={cn(inputClass, "border")}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "phoneLabel", language)}</label>
                  <PhoneInput
                    countries={countries}
                    country={dialCountry}
                    onCountryChange={(c) => {
                      setDialCountry(c.code);
                      setPhone((p) => formatNational(p.replace(/\D/g, ""), c.code));
                    }}
                    value={phone}
                    onChange={setPhone}
                    invalid={showPhoneError}
                    onBlur={() => setPhoneTouched(true)}
                    dialLabel={t("reseller", "dialCode", language)}
                    searchPlaceholder={t("reseller", "countrySearch", language)}
                    emptyText={t("reseller", "countryNone", language)}
                    inputClassName={cn(inputClass, "focus-within:ring-2 focus-within:ring-brand-accent focus-within:border-brand-accent")}
                  />
                  {showPhoneError && <p className="text-[11px] text-red-400">{t("reseller", "phoneInvalid", language)}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t("reseller", "partnerType", language)} *</label>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { id: "reseller" as const, label: t("reseller", "resellerOption", language), desc: t("reseller", "resellerDesc", language) },
                    { id: "referral" as const, label: t("reseller", "referralOption", language), desc: t("reseller", "referralDesc", language) },
                  ]).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setPartnerType(opt.id)}
                      aria-pressed={partnerType === opt.id}
                      className={cn(
                        "rounded-[12px] border px-3.5 py-3 text-left transition-all duration-200",
                        partnerType === opt.id
                          ? "border-brand-accent bg-brand-accent/10"
                          : "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12]"
                      )}
                    >
                      <span className={cn("text-sm font-medium block", partnerType === opt.id ? "text-white" : "text-white/50")}>
                        {opt.label}
                      </span>
                      <span className="text-[11px] text-white/30 leading-snug block mt-0.5">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                className={cn(
                  "w-full h-12 sm:h-[52px] text-sm font-medium rounded-[14px] transition-all duration-200",
                  isFormValid()
                    ? "bg-brand-accent hover:bg-brand-accent/90 text-white shadow-[0_0_24px_rgba(68,138,255,0.25)] hover:shadow-[0_0_32px_rgba(68,138,255,0.35)]"
                    : "bg-white/[0.06] text-white/25 cursor-not-allowed"
                )}
                disabled={!isFormValid() || loading}
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("reseller", "creatingAccount", language)}</>
                ) : (
                  <><UserPlus className="h-4 w-4 mr-2" />{t("reseller", "createBtn", language)}</>
                )}
              </Button>
            </form>
          </div>
        </div>
        </div>
      </section>
    </>
  );
};

export default RegisterPage;
